/**
 * Content Transformation Utilities
 * 
 * These functions handle the "mixing" of content based on slider positions.
 * Instead of just showing/hiding content, we blend, emphasize, and reveal
 * based on the mixer state.
 */

/**
 * Blend between two text variants based on a blend factor.
 * At 0, shows textA. At 1, shows textB. In between, shows a weighted combination.
 * 
 * For text, we use a threshold-based approach:
 * - 0-30: Show A with subtle indicator of B
 * - 30-70: Show transitional hybrid
 * - 70-100: Show B with subtle indicator of A
 */
export function blendText(textA, textB, blendFactor) {
  if (blendFactor <= 0.3) {
    return textA;
  } else if (blendFactor >= 0.7) {
    return textB;
  } else {
    // In the middle zone, we could show a hybrid or pick based on closest
    // For clean reading, we'll pick based on which side we're closer to
    return blendFactor < 0.5 ? textA : textB;
  }
}

/**
 * Get emphasis weights for different content aspects.
 * Returns an object with opacity/scale values for visual weighting.
 */
export function getEmphasisWeights(blendFactor) {
  return {
    primary: 1,
    secondary: 0.4 + (blendFactor * 0.3), // Varies between 0.4 and 0.7
    accent: blendFactor > 0.5 ? blendFactor : 1 - blendFactor,
  };
}

/**
 * Transform experience content based on mixer state
 */
export function transformExperience(experience, mixerState) {
  const techBusinessBlend = mixerState.technicalVsBusiness / 100;
  const icLeadershipBlend = mixerState.icVsLeadership / 100;
  const consultingEnterpriseBlend = mixerState.consultingVsEnterprise / 100;
  
  return {
    ...experience,
    displayDescription: blendText(
      experience.description.technical,
      experience.description.business,
      techBusinessBlend
    ),
    displayRole: blendText(
      experience.role.ic,
      experience.role.leadership,
      icLeadershipBlend
    ),
    displayContext: blendText(
      experience.context.consulting,
      experience.context.enterprise,
      consultingEnterpriseBlend
    ),
    // Visual emphasis based on mixer position
    emphasis: {
      technical: 1 - techBusinessBlend,
      business: techBusinessBlend,
      ic: 1 - icLeadershipBlend,
      leadership: icLeadershipBlend,
      consulting: 1 - consultingEnterpriseBlend,
      enterprise: consultingEnterpriseBlend,
    }
  };
}

/**
 * Transform project content based on mixer state
 */
export function transformProject(project, mixerState) {
  const techBusinessBlend = mixerState.technicalVsBusiness / 100;
  const icLeadershipBlend = mixerState.icVsLeadership / 100;
  const consultingEnterpriseBlend = mixerState.consultingVsEnterprise / 100;
  
  return {
    ...project,
    displayDescription: blendText(
      project.description.technical,
      project.description.business,
      techBusinessBlend
    ),
    displayRole: blendText(
      project.role.ic,
      project.role.leadership,
      icLeadershipBlend
    ),
    displayContext: blendText(
      project.context.consulting,
      project.context.enterprise,
      consultingEnterpriseBlend
    ),
    emphasis: {
      technical: 1 - techBusinessBlend,
      business: techBusinessBlend,
      ic: 1 - icLeadershipBlend,
      leadership: icLeadershipBlend,
      consulting: 1 - consultingEnterpriseBlend,
      enterprise: consultingEnterpriseBlend,
    }
  };
}

/**
 * Get the current "lens" label based on mixer state
 */
export function getCurrentLens(mixerState) {
  const lenses = [];
  
  // Technical vs Business
  if (mixerState.technicalVsBusiness < 35) {
    lenses.push('Technical Deep Dive');
  } else if (mixerState.technicalVsBusiness > 65) {
    lenses.push('Business Impact');
  }
  
  // IC vs Leadership
  if (mixerState.icVsLeadership < 35) {
    lenses.push('Hands-On Execution');
  } else if (mixerState.icVsLeadership > 65) {
    lenses.push('Leadership & Ownership');
  }
  
  // Consulting vs Enterprise
  if (mixerState.consultingVsEnterprise < 35) {
    lenses.push('Consulting Mindset');
  } else if (mixerState.consultingVsEnterprise > 65) {
    lenses.push('Enterprise Context');
  }
  
  return lenses.length > 0 ? lenses : ['Balanced View'];
}

/**
 * Filter and sort content based on relevance to current mixer state
 */
export function filterByRelevance(items, mixerState) {
  const consultingBlend = mixerState.consultingVsEnterprise / 100;
  
  return [...items].sort((a, b) => {
    // Prioritize items that match the current consulting/enterprise lean
    const aScore = a.type === 'consulting' ? (1 - consultingBlend) : consultingBlend;
    const bScore = b.type === 'consulting' ? (1 - consultingBlend) : consultingBlend;
    return bScore - aScore;
  });
}

/**
 * Get summary headline based on mixer state
 */
export function getSummary(profile, mixerState) {
  const techBusiness = mixerState.technicalVsBusiness / 100;
  const icLeadership = mixerState.icVsLeadership / 100;
  
  // Create a blended summary based on mixer positions
  if (techBusiness < 0.35 && icLeadership < 0.35) {
    return profile.summary.ic;
  } else if (techBusiness < 0.35 && icLeadership > 0.65) {
    return profile.summary.technical;
  } else if (techBusiness > 0.65 && icLeadership < 0.35) {
    return profile.summary.business;
  } else if (techBusiness > 0.65 && icLeadership > 0.65) {
    return profile.summary.leadership;
  }
  
  // Default balanced summary
  return icLeadership > 0.5 ? profile.summary.leadership : profile.summary.ic;
}
